import { fillTemplate, smsLength, TEMPLATE_FIELDS, type TemplateValues } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Icon } from '../../components/Icon.js'
import { Modal, ModalCloseButton } from '../../components/Modal.js'
import { Button, Card, CardHeader, EmptyState, Field, Input, SkeletonRows, Textarea } from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { relativeTime } from '../../lib/format.js'
import { errorMessage, fieldErrors, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

type Template = RouterOutputs['messageTemplates']['list'][number]
type Channel = 'EMAIL' | 'SMS'

const EXAMPLES: TemplateValues = Object.fromEntries(TEMPLATE_FIELDS.map((field) => [field.key, field.example]))

const CHANNELS: { channel: Channel; title: string; empty: string }[] = [
  {
    channel: 'EMAIL',
    title: 'Email',
    empty: 'The emails the office writes most — a quote follow-up, a booking confirmation, a payment reminder.',
  },
  { channel: 'SMS', title: 'SMS', empty: 'Short texts: on my way, running late, reminder for tomorrow.' },
]

/**
 * Saved emails and texts, offered by the compose window's Templates button.
 *
 * A template's `{{fields}}` are filled in the window from the job it is used
 * on, never by the model, and one with nothing behind it stays visible as
 * `[quote number]` so it cannot leave the building blank. The order here is
 * the order in the menu. Decision 95.
 */
export function TemplatesPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  // `null` is closed; otherwise the channel of a new one or the template being edited.
  const [editing, setEditing] = useState<{ channel: Channel; template: Template | null } | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)

  const templates = useQuery(trpc.messageTemplates.list.queryOptions({}))
  const refresh = () => void queryClient.invalidateQueries(trpc.messageTemplates.pathFilter())
  const reorder = useMutation(trpc.messageTemplates.reorder.mutationOptions({ onSettled: refresh }))
  const remove = useMutation(
    trpc.messageTemplates.remove.mutationOptions({
      onSuccess: () => setConfirming(null),
      onSettled: refresh,
    }),
  )

  const move = (list: Template[], index: number, by: -1 | 1) => {
    const ids = list.map((template) => template.id)
    const [moved] = ids.splice(index, 1)
    ids.splice(index + by, 0, moved!)
    reorder.mutate({ channel: list[0]!.channel as Channel, ids })
  }

  const error = reorder.error ?? remove.error

  return (
    <Card>
      <CardHeader title="Message templates" />
      <div className="flex flex-col gap-5 px-[var(--row-pad-x)] py-3">
        <p className="max-w-[560px] text-xs text-ink-500">
          Offered by Templates in every email and SMS window. Fields such as {'{{contact.firstName}}'} are filled from
          the job it is used on; anything with nothing behind it is left showing so it is caught before sending.
        </p>

        {templates.isPending ? (
          <SkeletonRows rows={4} />
        ) : templates.error ? (
          <p className="text-[13px] text-red-700">{errorMessage(templates.error)}</p>
        ) : (
          CHANNELS.map(({ channel, title, empty }) => {
            const list = templates.data.filter((template) => template.channel === channel)
            return (
              <section key={channel} aria-label={`${title} templates`} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[13px] font-semibold text-ink-900">
                    {title} <span className="font-normal text-ink-500">· {list.length}</span>
                  </h3>
                  <Button size="sm" variant="primary" onClick={() => setEditing({ channel, template: null })}>
                    New {channel === 'EMAIL' ? 'email' : 'SMS'} template
                  </Button>
                </div>
                {list.length === 0 ? (
                  <EmptyState title="None yet" description={empty} />
                ) : (
                  <ul className="flex flex-col divide-y divide-ink-100 rounded-lg ring-1 ring-ink-200">
                    {list.map((template, index) => (
                      <li key={template.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            aria-label={`Move ${template.name} up`}
                            disabled={index === 0 || reorder.isPending}
                            onClick={() => move(list, index, -1)}
                            className="grid size-5 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                          >
                            <Icon name="back" className="size-3.5 rotate-90" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${template.name} down`}
                            disabled={index === list.length - 1 || reorder.isPending}
                            onClick={() => move(list, index, 1)}
                            className="grid size-5 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                          >
                            <Icon name="back" className="size-3.5 -rotate-90" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditing({ channel, template })}
                          className="flex min-w-0 flex-1 flex-col items-start text-left"
                        >
                          <span className="text-[13px] font-semibold text-ink-900">{template.name}</span>
                          <span className="line-clamp-1 text-xs text-ink-500">
                            {[template.subject, template.bodyText?.replace(/\s+/g, ' '), `changed ${relativeTime(template.updatedAt)}`]
                              .filter(Boolean)
                              .join(' — ')}
                          </span>
                        </button>
                        {confirming === template.id ? (
                          <>
                            <span className="text-xs text-ink-600">Remove it?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={remove.isPending}
                              onClick={() => remove.mutate({ id: template.id })}
                            >
                              Remove
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                              Keep
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" onClick={() => setEditing({ channel, template })}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Remove ${template.name}`}
                              onClick={() => setConfirming(template.id)}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })
        )}
        {error ? <p className="text-xs text-red-600">{errorMessage(error)}</p> : null}
      </div>

      {editing ? (
        <TemplateEditor
          key={editing.template?.id ?? `new-${editing.channel}`}
          channel={editing.channel}
          template={editing.template}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh()
            setEditing(null)
          }}
        />
      ) : null}
    </Card>
  )
}

function TemplateEditor({
  channel,
  template,
  onClose,
  onSaved,
}: {
  channel: Channel
  template: Template | null
  onClose: () => void
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const [name, setName] = useState(template?.name ?? '')
  const [subject, setSubject] = useState(template?.subject ?? '')
  const [body, setBody] = useState(template?.bodyText ?? '')
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  // Where a field from the palette goes: the box that last had the cursor.
  const target = useRef<'subject' | 'body'>('body')
  const save = useMutation(trpc.messageTemplates.upsert.mutationOptions({ onSuccess: onSaved }))
  const errors = fieldErrors(save.error)
  const email = channel === 'EMAIL'

  const insert = (key: string) => {
    const token = `{{${key}}}`
    const into = email && target.current === 'subject' ? 'subject' : 'body'
    const element = into === 'subject' ? subjectRef.current : bodyRef.current
    const current = into === 'subject' ? subject : body
    const start = element?.selectionStart ?? current.length
    const end = element?.selectionEnd ?? current.length
    const next = current.slice(0, start) + token + current.slice(end)
    if (into === 'subject') setSubject(next)
    else setBody(next)
    requestAnimationFrame(() => {
      element?.focus()
      element?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const preview = fillTemplate(body, EXAMPLES).text
  const previewSubject = email && subject ? fillTemplate(subject, EXAMPLES).text : null
  const length = email ? null : smsLength(preview)
  const title = template ? `Edit ${template.name}` : email ? 'New email template' : 'New SMS template'

  return (
    <Modal open onClose={onClose} labelledBy="template-editor-title" className="max-w-[880px]">
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate({
            id: template?.id ?? null,
            name,
            channel,
            subject: email ? subject : null,
            body,
          })
        }}
      >
        <div className="flex items-center gap-3 border-b border-ink-200 px-5 py-3">
          <span className="grid size-8 place-items-center rounded-lg bg-brand-50 text-brand-700" aria-hidden="true">
            <Icon name={email ? 'mail' : 'sms'} className="size-4" />
          </span>
          <h2 id="template-editor-title" className="flex-1 text-[15px] font-semibold text-ink-900">
            {title}
          </h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-5 md:grid-cols-[1fr_220px]">
          <div className="flex flex-col gap-3">
            <Field label="Name" htmlFor="template-name" error={errors.name} hint="What it is called in the Templates list.">
              <Input id="template-name" value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            {email ? (
              <Field label="Subject" htmlFor="template-subject" error={errors.subject}>
                <Input
                  id="template-subject"
                  ref={subjectRef}
                  value={subject}
                  onFocus={() => {
                    target.current = 'subject'
                  }}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </Field>
            ) : null}
            <Field label="Message" htmlFor="template-body" error={errors.body}>
              <Textarea
                id="template-body"
                ref={bodyRef}
                rows={email ? 10 : 5}
                value={body}
                onFocus={() => {
                  target.current = 'body'
                }}
                onChange={(event) => setBody(event.target.value)}
              />
            </Field>

            <section aria-label="Preview" className="rounded-lg bg-ink-50 p-3 ring-1 ring-inset ring-ink-200">
              <p className="mb-1 flex items-center justify-between text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                <span>Preview, with made-up details</span>
                {length ? (
                  <span className="font-normal tracking-normal normal-case tabular-nums">
                    {length.characters} characters · {length.parts || 1} SMS
                  </span>
                ) : null}
              </p>
              {previewSubject ? <p className="text-[13px] font-semibold text-ink-900">{previewSubject}</p> : null}
              <p className={cn('text-[13px] whitespace-pre-wrap', preview ? 'text-ink-800' : 'text-ink-400')}>
                {preview || 'Nothing written yet.'}
              </p>
            </section>
          </div>

          <aside aria-label="Fields" className="flex flex-col gap-2">
            <p className="text-xs font-medium text-ink-700">Fields</p>
            <p className="text-xs text-ink-500">Click one to put it where the cursor is.</p>
            <ul className="flex flex-wrap gap-1.5 md:flex-col md:flex-nowrap">
              {TEMPLATE_FIELDS.map((field) => (
                <li key={field.key}>
                  <button
                    type="button"
                    // Keep the cursor where it was in the box being written.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insert(field.key)}
                    title={`e.g. ${field.example}`}
                    className="w-full rounded-md bg-white px-2 py-1 text-left text-xs text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-brand-50 hover:ring-brand-300"
                  >
                    {field.label}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-5 py-3">
          {save.error && Object.keys(errors).length === 0 ? (
            <p role="alert" className="mr-auto text-xs text-red-600">
              {errorMessage(save.error)}
            </p>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Save template
          </Button>
        </div>
      </form>
    </Modal>
  )
}
